import { Header, Switch } from '@bp/ui-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SolverEngine } from '../../../types/planung-graphql-client-defs';
import { useTimetableStore } from '../TimetableProvider';
import styles from './wizard.module.scss';

// Timefold speed presets (the backend maps the key to a spentLimit); shown as three tiles.
const SPEED_OPTIONS = ['fast', 'balanced', 'thorough'] as const;

export const WizardStep2Options: FC = observer(() => {
  const { t } = useTranslation();
  const pinboardStore = useTimetableStore();
  const solver = pinboardStore.solverStore;

  const task = solver.task;
  const isGenerate = task === 'GENERATE';
  const isOptimize = task === 'OPTIMIZE';
  const isAssignRooms = task === 'ASSIGN_ROOMS';

  const selectEngine = (engine: SolverEngine) => () => solver.setSelectedEngine(engine);

  const has = (engine: SolverEngine) => solver.availableEngines.includes(engine);
  // With a single available engine there is nothing to pick — it is auto-selected upstream and
  // we drop straight to the generator options.
  const showEngineChoice = solver.availableEngines.length > 1;

  return (
    <div style={{ display: 'block' }}>
      {isGenerate && showEngineChoice && (
        <>
          <Header headline={t('pinboard.generate.wizard.step2.title')} size='s' noMargin />
          <p className={styles['section-sub']}>{t('pinboard.generate.wizard.step2.sub')}</p>

          <div className={styles['tile-row']}>
            {has(SolverEngine.Timefold) && (
              <button
                type='button'
                className={classNames(styles.tile, {
                  [styles.active]: solver.selectedEngine === SolverEngine.Timefold,
                })}
                onClick={selectEngine(SolverEngine.Timefold)}
              >
                <span className={styles['badge-rec']}>{t('solver.engine.recommended')}</span>
                <div className={styles.name}>{t('solver.engine.timefold.name')}</div>
                <div className={styles.desc}>{t('solver.engine.timefold.desc')}</div>
              </button>
            )}
            {has(SolverEngine.Mathplan) && (
              <button
                type='button'
                className={classNames(styles.tile, {
                  [styles.active]: solver.selectedEngine === SolverEngine.Mathplan,
                })}
                onClick={selectEngine(SolverEngine.Mathplan)}
              >
                <div className={styles.name}>{t('solver.engine.mathplan.name')}</div>
                <div className={styles.desc}>{t('solver.engine.mathplan.desc')}</div>
              </button>
            )}
          </div>
        </>
      )}

      {(!isGenerate || solver.selectedEngine === SolverEngine.Timefold) && (
        <>
          <div className={styles.eyebrow}>{t('pinboard.generate.wizard.step2.speed')}</div>
          <div className={styles['tile-row-3']}>
            {SPEED_OPTIONS.filter((speed) => !(isAssignRooms && speed === 'thorough')).map((speed) => (
              <button
                key={speed}
                type='button'
                className={classNames(styles.tile, { [styles.active]: solver.generatorOptions.speed === speed })}
                onClick={() => solver.setSpeed(speed)}
              >
                <div className={styles.name}>{t(`pinboard.generate.wizard.step2.speedOption.${speed}.name`)}</div>
                <div className={styles.desc}>{t(`pinboard.generate.wizard.step2.speedOption.${speed}.desc`)}</div>
              </button>
            ))}
          </div>
        </>
      )}

      {(isGenerate || isOptimize) && (
        <div className={styles.eyebrow}>{t('pinboard.generate.wizard.step2.options')}</div>
      )}

      {isGenerate && (
        <div className={styles['toggle-list']}>
          <div className={styles['toggle-row']}>
            <div className={styles['toggle-body']}>
              <div className={styles.label}>{t('pinboard.generate.keepCards')}</div>
              <div className={styles.hint}>{t('pinboard.generate.wizard.step2.keepCardsHint')}</div>
            </div>
            <Switch
              name='wizard-keepCards'
              direction='right'
              checked={solver.generatorOptions.keepExistingPositions}
              onChange={() => solver.setKeepExistingPositions(!solver.generatorOptions.keepExistingPositions)}
            />
          </div>

          <div className={styles['toggle-row']}>
            <div className={styles['toggle-body']}>
              <div className={styles.label}>{t('pinboard.generate.withRooms')}</div>
              <div className={styles.hint}>{t('pinboard.generate.wizard.step2.withRoomsHint')}</div>
            </div>
            <Switch
              name='wizard-withRooms'
              direction='right'
              checked={solver.generatorOptions.includeRoomResources}
              onChange={() => solver.setIncludeRoomResources(!solver.generatorOptions.includeRoomResources)}
            />
          </div>

          <div className={styles['toggle-row']}>
            <div className={styles['toggle-body']}>
              <div className={styles.label}>{t('pinboard.generate.considerTeachingBlock')}</div>
              <div className={styles.hint}>{t('pinboard.generate.wizard.step2.teachingBlockHint')}</div>
              {solver.generatorOptions.includeSubjectContainer ? (
                <div className={styles['hint-box']}>{t('pinboard.generate.subjectContainerHint')}</div>
              ) : null}
            </div>
            <Switch
              name='wizard-teachingBlock'
              direction='right'
              checked={solver.generatorOptions.includeSubjectContainer}
              onChange={() => solver.setIncludeSubjectContainer(!solver.generatorOptions.includeSubjectContainer)}
            />
          </div>
        </div>
      )}

      {isOptimize && (
        <div className={styles['toggle-list']}>
          <div className={styles['toggle-row']}>
            <div className={styles['toggle-body']}>
              <div className={styles.label}>{t('pinboard.optimize.crossClass.label')}</div>
            </div>
            <Switch
              name='optimize-crossClass'
              direction='right'
              checked={solver.allowMoveOtherClasses}
              onChange={() => solver.setAllowMoveOtherClasses(!solver.allowMoveOtherClasses)}
            />
          </div>

          <div className={styles['toggle-row']}>
            <div className={styles['toggle-body']}>
              <div className={styles.label}>{t('pinboard.generate.withRooms')}</div>
              <div className={styles.hint}>{t('pinboard.generate.wizard.step2.withRoomsHint')}</div>
            </div>
            <Switch
              name='optimize-withRooms'
              direction='right'
              checked={solver.generatorOptions.includeRoomResources}
              onChange={() => solver.setIncludeRoomResources(!solver.generatorOptions.includeRoomResources)}
            />
          </div>
        </div>
      )}

      {isAssignRooms && <div className={styles['hint-box']}>{t('pinboard.assignRooms.wizard.info')}</div>}
    </div>
  );
});
